import { useEffect } from "react";
import CharlestonHeroSection from "./components/HeroSection";
import CharlestonStickyNav from "./components/StickyNav";
import CharlestonProcessingTimeSection from "./components/ProcessingTimeSection";
import CharlestonServicesSection from "./components/ServicesSection";
import CharlestonLocalAreaSection from "./components/LocalAreaSection";
import CharlestonTestimonialsSection from "./components/TestimonialsSection";
import CharlestonAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import CharlestonComparisonSection from "./components/ComparisonSection";
import CharlestonMistakesSection from "./components/MistakesSection";
import CharlestonHowItWorksSection from "./components/HowItWorksSection";
import CharlestonFAQSection, { faqs } from "./components/FAQSection";
import CharlestonHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import CharlestonFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function CharlestonPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Charleston SC | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/charleston/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Charleston, SC — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Charleston, Mount Pleasant, Summerville, and all of coastal South Carolina.");
    setMeta("geo.placename", "Charleston, South Carolina");
    setMeta("geo.region", "US-SC");
    setMeta("geo.position", "32.7765;-79.9311");
    setMeta("ICBM", "32.7765, -79.9311");
    setMeta("keywords", "expedited passport Charleston SC, emergency passport Charleston, same day passport South Carolina, fast passport Charleston, passport renewal Lowcountry");

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "South Carolina", item: "https://fastpassportcenter.com/south-carolina/" },
          { "@type": "ListItem", position: 3, name: "Charleston Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Charleston and coastal South Carolina travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Charleston", containedInPlace: { "@type": "State", name: "South Carolina" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <CharlestonHeroSection />
      <CharlestonStickyNav />
      <CharlestonProcessingTimeSection />
      <CharlestonServicesSection />
      <CharlestonLocalAreaSection />
      <CharlestonTestimonialsSection />
      <CharlestonAcceptanceFacilitiesSection />
      <CharlestonComparisonSection />
      <CharlestonMistakesSection />
      <CharlestonHowItWorksSection />
      <CharlestonFAQSection />
      <CharlestonHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/charleston" state="SC" />
      <CharlestonFinalCTA />
    </div>
  );
}